/* eslint-disable no-console */
import {
  isDecimal,
  formatDecimal,
  parseStringToFloat,
  DROPDOWN_OPTION_VALUE_NO_SELECTION
} from ".";
import type { ControllerFieldState } from "react-hook-form";

/*
  used for react-hook-form elements (inputfield, rextarea, dropdown)
*/
export const formatReturnValue = (
  value: string | string[],
  returnType: "string" | "number" | "auto" | "currency" | "boolean" | "code",
  isDropdown = true,
  lng: string
):
  | string
  | string[]
  | number
  | number[]
  | (number | null)[]
  | boolean
  | boolean[] => {
  // not an Array
  if (Array.isArray(value) === false) {
    const valueStr = value as string;

    switch (returnType) {
      // integer
      case "number": {
        const num = Number(valueStr);
        const isValidNumber = !isNaN(num);
        const isIntNumber = Number.isInteger(num);

        if (!isValidNumber || !isIntNumber) {
          return isDropdown ? DROPDOWN_OPTION_VALUE_NO_SELECTION : valueStr;
        }

        // if length is 0, return null
        return valueStr.length > 0
          ? num
          : isDropdown
            ? DROPDOWN_OPTION_VALUE_NO_SELECTION
            : 0;
      }
      case "currency":
        if (isDecimal(value as string, lng)) {
          const parsedFloat = parseStringToFloat(valueStr, lng);
          const isValidNumber = !isNaN(parsedFloat);
          if (isValidNumber) return formatDecimal(parsedFloat, lng, false);
        }
        return valueStr;
      case "boolean":
        return valueStr === "false" || valueStr === "0" ? false : true;
      case "code":
        return valueStr.replace(/\s+/g, "").toUpperCase();
      default:
        return valueStr.toString() as string;
    }
  }

  // is an Array
  const valueArr = value as string[];
  switch (returnType) {
    case "number":
      // if length is 0, return null
      return valueArr.map((num) =>
        num.length > 0 ? Number(num) : DROPDOWN_OPTION_VALUE_NO_SELECTION
      ) as (number | null)[];
    case "boolean":
      return valueArr.map((bool) =>
        bool === "false" || bool === "0" ? false : true
      ) as boolean[];
    default:
      return valueArr.map((str) => str.toString()) as string[];
  }
};

/*
  used for react-hook-form elements. Returns customer set notification or error message
*/
export const getNotificationProps = (
  notification: string | undefined,
  notificationType: "default" | "error" | "success" | "warning" = "default",
  { isTouched, error }: ControllerFieldState,
  disabled: boolean,
  ignoreTouched: boolean
) => {
  let internalType = notificationType;
  let internalText: string | undefined = notification;

  // overwrite with error message (if not disabled)
  if (error && !disabled) {
    if (isTouched || (!isTouched && ignoreTouched)) {
      internalType = "error";
      internalText = error?.message;
    }
  }

  return { _notificationType: internalType, _notification: internalText };
};

export const getNotificationClassNames = (
  notificationType: "default" | "error" | "success" | "warning"
) => {
  switch (notificationType) {
    case "error":
      return "label-text-alt text-error";
    case "warning":
      return "label-text-alt text-warning";
    case "success":
      return "label-text-alt text-success";
    default:
      return "label-text-alt";
  }
};
